import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { EInterventionType } from 'src/app/domain/planning/enum/intervention-type';
import { ETypeSubstrat as IBGNSubstrat } from '../../../domain/IBGN/enum/type-substrat';
import { ETypeSubstrat as MPCESubstrat } from '../../../domain/MPCE/enum/type-substrat';
import { ClasseVitesse } from '../../../domain/Shared/classe-vitesse';
import { PrelevementVitesse } from '../../../domain/Shared/prelevement-vitesse';
import { SampleTakingModel } from '../../../models/new-substrat-model';
import { SimpleListModel } from '../../../models/simple-list-model';
import { StringConverter } from '../string-converter';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-substrat-and-vitesse-dialog',
  templateUrl: './substrat-and-vitesse-dialog.component.html',
  styleUrls: ['./substrat-and-vitesse-dialog.component.scss']
})
export class SubstratAndVitesseDialogComponent implements OnInit {
  Selection = [];
  SampleTaking: SampleTakingModel = new SampleTakingModel();
  PercentageControl = new UntypedFormControl('', [Validators.required, Validators.min(0), Validators.max(100)]);
  public IBGNSubstratList: SimpleListModel[] =
    Object.values(IBGNSubstrat)
      .filter(x => !isNaN(Number(x)))
      .map(x => {
        const n = new SimpleListModel();
        n.idx = x as number;
        n.s = StringConverter.getIBGNSubstratName(x as IBGNSubstrat);
        return n;
      });

  public MPCESubstratList: SimpleListModel[] =
    Object.values(MPCESubstrat)
      .filter(x => !isNaN(Number(x)))
      .map(x => {
        const n = new SimpleListModel();
        n.idx = x as number;
        n.s = StringConverter.getMPCESubstratName(x as MPCESubstrat);
        return n;
      });

  VitesseList: ClasseVitesse[] = [];
  InterventionType: EInterventionType;

  get CanAdd() {
    return this.SampleTaking.Type !== undefined &&
      (this.PercentageControl.valid || (this.PercentageControl.disabled && this.SampleTaking.Presence === true)) &&
      this.SampleTaking.PrelevementVitesses.length >= 1 && this.isValid();
  }

  constructor(
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<SubstratAndVitesseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data) {
    this.SampleTaking = Object.assign(this.SampleTaking, data.stm);
    this.SampleTaking.PrelevementVitesses = Object.assign([], data.stm.PrelevementVitesses);
    this.InterventionType = data.type;

    _.remove(data.alreadyPicked, s => s === this.SampleTaking.Type); // Let the current selected type of substrat in the list
    if (this.InterventionType === EInterventionType.MPCE) {
      _.remove(this.MPCESubstratList, s => data.alreadyPicked.includes(s.idx));
      this.VitesseList.push(ClasseVitesse.N1);
      this.VitesseList.push(ClasseVitesse.N3);
      this.VitesseList.push(ClasseVitesse.N5);
      this.VitesseList.push(ClasseVitesse.N6);
    } else { // IBGN
      _.remove(this.IBGNSubstratList, s => data.alreadyPicked.includes(s.idx));
      this.VitesseList.push(ClasseVitesse.N2);
      this.VitesseList.push(ClasseVitesse.N4);
      this.VitesseList.push(ClasseVitesse.N5);
      this.VitesseList.push(ClasseVitesse.N3);
      this.VitesseList.push(ClasseVitesse.N1);
    }

    this.SampleTaking.PrelevementVitesses.forEach(x => {
      this.Selection[`${x.Vitesse - 1}`] = [`${x.Representativite}`];
    });

    if (this.SampleTaking.Presence) {
      this.PercentageControl.disable();
    }
  }

  ngOnInit(): void {
    this.PercentageControl.markAsTouched();
  }

  toggleChange(event, vitesse: ClasseVitesse) {
    const toggle = event.source;
    if (toggle) {
      const group = toggle.buttonToggleGroup;
      if (event.value.some(item => item === toggle.value)) {
        group.value = [toggle.value];
        if (this.SampleTaking.PrelevementVitesses.find(x => x.Vitesse === vitesse)) {
          _.remove(this.SampleTaking.PrelevementVitesses, this.SampleTaking.PrelevementVitesses.find(x => x.Vitesse === vitesse));
        }
        const pv = new PrelevementVitesse();
        pv.Vitesse = vitesse;
        pv.Representativite = Number(toggle.value);
        this.SampleTaking.PrelevementVitesses.push(pv);
      }
    } else {
      _.remove(this.SampleTaking.PrelevementVitesses, this.SampleTaking.PrelevementVitesses.find(x => x.Vitesse === vitesse));
    }
    this.SampleTaking.PrelevementVitesses = _.sortBy(this.SampleTaking.PrelevementVitesses, (x) => x.Vitesse);
  }

  setPresenceForSampleTaking() {
    if (this.SampleTaking.Presence) {
      this.PercentageControl.enable();
      this.SampleTaking.Pourcentage = null;
    } else {
      this.PercentageControl.disable();
      this.SampleTaking.Pourcentage = null;
      this.Selection = [];
      this.SampleTaking.PrelevementVitesses = [];
    }
  }

  checkIfAlreadyPicked(cv: ClasseVitesse, representativite: number) {
    const oldOne = this.SampleTaking.PrelevementVitesses.find(x => x.Representativite === representativite && cv !== x.Vitesse);
    if (oldOne) {
      this.Selection[oldOne.Vitesse - 1] = null;
      _.remove(this.SampleTaking.PrelevementVitesses, this.SampleTaking.PrelevementVitesses.find(x => x.Vitesse === oldOne.Vitesse));
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  isValid() {

    // TODO FIX ordering
    for (let i = 0; i < this.SampleTaking.PrelevementVitesses.length; i++) {

      if (!this.SampleTaking.PrelevementVitesses.some(x => x.Representativite === i + 1)) {
        return false;
      }
    }

    return true;
  }

  onConfirm() {
    this.dialogRef.close(this.SampleTaking);
  }
}
